/**
 * HFS product page helpers (W25: PDP-02, JS-19, PDP-11, CSS-09, PDP-12, PDP-44).
 *
 * Shared by the two variant pickers (snippets/custom-product-variant-picker.liquid and global.js
 * VariantSelects/VariantRadios):
 *   window.hfsPdp.announce(message)                     // polite live-region message
 *   window.hfsPdp.setAddState(sectionId, unavailable, text)   // Add button: aria-disabled + text
 *   window.hfsPdp.setOptionError(name, show)            // "Please select an option" for one option group
 *   window.hfsPdp.sizeToggle(initialMode, onChange)     // EU/US size system toggle element
 *
 * It also keeps a visually-hidden " - sold out" hint inside the label of every unavailable option
 * (radios carrying the .disabled class), whichever script toggles that class.
 */
(function () {
  'use strict';

  if (window.hfsPdp) return;

  var strings = function () {
    return window.variantStrings || {};
  };

  /* Live region, created once so later text changes are announced. */
  var statusEl = null;
  function statusRegion() {
    if (statusEl && document.body.contains(statusEl)) return statusEl;
    statusEl = document.getElementById('hfs-pdp-status');
    if (!statusEl) {
      statusEl = document.createElement('div');
      statusEl.id = 'hfs-pdp-status';
      statusEl.className = 'visually-hidden';
      statusEl.setAttribute('role', 'status');
      statusEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(statusEl);
    }
    return statusEl;
  }

  var announceTimer = null;
  function announce(message) {
    var region = statusRegion();
    clearTimeout(announceTimer);
    region.textContent = '';
    if (!message) return;
    // Clearing first makes a repeated message announce again.
    announceTimer = setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  /* Add to cart button state. aria-disabled keeps the button focusable (so its "Sold out" / "Unavailable" text
     is discoverable); product-form.js refuses to submit while it is set or data-hfs-unavailable is present. */
  function setAddState(sectionId, unavailable, text) {
    var button = document.getElementById('ProductSubmitButton-' + sectionId);
    if (!button) return;
    var label = button.querySelector('span');
    button.classList.toggle('sold_out', !!unavailable);
    // Dynamic checkout ("Buy it now") is not offered for a sold-out/unavailable variant
    var payment = button.parentElement && button.parentElement.querySelector('.shopify-payment-button');
    if (payment) payment.style.display = unavailable ? 'none' : '';
    if (unavailable) {
      button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-hfs-unavailable', '');
      if (label && text) label.textContent = text;
    } else {
      button.removeAttribute('disabled');
      button.removeAttribute('data-hfs-unavailable');
      // product-form.js owns aria-disabled while a request is in flight.
      var form = button.closest('product-form');
      if (!form || !form.isSubmitting) button.removeAttribute('aria-disabled');
      if (label) label.textContent = strings().addToCart || 'Add to cart';
    }
  }

  /* "Please select an option" message for one option group (Color, Width, Size). Each message is tagged
     data-option-error="<name>" and has its own unique id; the group's radios point at it while it is shown. */
  function setOptionError(name, show, root) {
    var messages = Array.from((root || document).querySelectorAll('[data-option-error]')).filter(function (message) {
      return message.dataset.optionError === name;
    });
    messages.forEach(function (message) {
      message.style.display = show ? '' : 'none';
      var group = message.closest('fieldset');
      if (!group) return;
      group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        if (show) {
          radio.setAttribute('aria-invalid', 'true');
          var descriptions = new Set((radio.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
          descriptions.add(message.id);
          radio.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
        } else {
          radio.removeAttribute('aria-invalid');
          var remaining = (radio.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) { return id && id !== message.id; });
          if (remaining.length) radio.setAttribute('aria-describedby', remaining.join(' '));
          else radio.removeAttribute('aria-describedby');
        }
      });
    });
    return messages.length > 0;
  }

  /* Unavailable option hint. */
  function syncOption(input) {
    var label = input.labels && input.labels[0];
    if (!label) return;
    var unavailable = input.classList.contains('disabled') || input.disabled;
    var hint = label.querySelector('.label-unavailable');
    if (unavailable && !hint) {
      hint = document.createElement('span');
      hint.className = 'visually-hidden label-unavailable';
      hint.textContent = ' – ' + (strings().soldOut || 'Sold out').toLowerCase();
      label.appendChild(hint);
    } else if (!unavailable && hint) {
      hint.remove();
    }
  }

  function syncAllOptions(root) {
    (root || document).querySelectorAll('.product-form__input input[type="radio"]').forEach(syncOption);
  }

  function watchOptions() {
    var root = document.body;
    root.querySelectorAll('[data-hfs-split-picker]').forEach(mountSplitPicker);
    syncAllOptions(root);
    if (!window.MutationObserver) return;
    new MutationObserver(function () {
      syncAllOptions(root);
      root.querySelectorAll('[data-hfs-split-picker]').forEach(mountSplitPicker);
    }).observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'disabled'],
    });
  }

  // Split products store each colour as a separate Shopify product. Never keep a previous product's id
  // live while loading another colour. Match every option by its actual position, not a Width/Size assumption.
  function mountSplitPicker(root) {
    if (root.hfsSplitPicker) return;
    var sectionId = root.dataset.section;
    var infoRoot = root.closest('product-info');
    var renderingSectionId = infoRoot?.dataset.originalSection || sectionId;
    var section = root.closest('quick-add-modal') || root.closest('section') || root.parentElement;
    var form = document.getElementById(root.dataset.formId);
    var optionsRoot = root.querySelector('#sub-variant-options');
    if (!form || !optionsRoot) return;
    root.hfsSplitPicker = true;
    var colourInputs = Array.from(root.querySelectorAll('input[data-option-name]'));
    var selected = {};
    var product = null;
    var loadToken = 0;
    var detailToken = 0;
    var controller;
    var colourData = {}; // handle -> Promise of that colour's product JSON (sold-out recovery)
    var altToken = 0;
    var altBox = null;
    var ALT_LIMIT = 12;
    var requested = null; // HFS (M1): the link's other options, chosen at the first options render (see the end)
    var baseUrl = (root.dataset.rootUrl || '/').replace(/\/?$/, '/');
    var optionKey = function (name) { return String(name).toLowerCase(); };
    var chosenColour = function () { return colourInputs.find(function (input) { return input.checked; }); };
    var productUrl = function (handle) { return baseUrl + 'products/' + encodeURIComponent(handle); };
    var setId = function (id) {
      form.querySelector('[name="id"]').value = id || '';
    };
    var clearSelection = function (nothingChosen) {
      detailToken++;
      setId('');
      if (nothingChosen) {
        // HFS (FBL-006): nothing chosen yet is not "Unavailable": neutral label, no sold_out class. data-hfs-unavailable
        // stays, so product-form.js reports the missing group (showMissingOptions) instead of adding.
        setAddState(sectionId, true, strings().chooseOptions || 'Choose options');
        var addButton = document.getElementById('ProductSubmitButton-' + sectionId);
        if (addButton) addButton.classList.remove('sold_out');
      } else {
        setAddState(sectionId, true, strings().unavailable || 'Unavailable');
      }
      var paymentId = document.querySelector('#product-form-installment-' + CSS.escape(sectionId) + ' [name="id"]');
      if (paymentId) paymentId.value = '';
    };
    function status(message, retry) {
      optionsRoot.replaceChildren();
      var text = document.createElement('p');
      text.setAttribute('role', 'status');
      text.textContent = message;
      optionsRoot.appendChild(text);
      if (retry) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button--secondary';
        button.textContent = 'Retry loading options';
        button.addEventListener('click', loadProduct);
        optionsRoot.appendChild(button);
      }
      announce(message);
    }
    /* HFS (general review 2026-10-01, sold-out recovery): each colour is its own product, so the picker cannot mark
       which colours have the chosen size and width. When that choice is sold out (or not made) in this colour, the
       other colours are looked up and the ones that have this exact size and width available are offered. Lookups
       start only on a sold-out or unavailable choice, are cached for the page, and a newer selection discards
       older answers. */
    function colourHandle(input) {
      return root.dataset.productHandle + (input.dataset.subproduct ? '-' + input.dataset.subproduct : '');
    }
    function colourProduct(input) {
      var handle = colourHandle(input);
      if (!colourData[handle]) {
        colourData[handle] = fetch(productUrl(handle) + '.js')
          .then(function (response) { if (!response.ok) throw new Error('Colour unavailable'); return response.json(); })
          .catch(function () { delete colourData[handle]; return null; });
      }
      return colourData[handle];
    }
    function hasSelectionAvailable(data, wanted) {
      if (!data || !Array.isArray(data.variants) || !Array.isArray(data.options)) return false;
      var positions = {};
      data.options.forEach(function (option, i) { positions[optionKey(option && option.name != null ? option.name : option)] = i; });
      var keys = Object.keys(wanted);
      if (keys.some(function (key) { return !(key in positions); })) return false;
      return data.variants.some(function (variant) {
        return variant.available && keys.every(function (key) { return String(variant.options[positions[key]]) === String(wanted[key]); });
      });
    }
    function clearAlternatives() {
      altToken++;
      if (altBox) { altBox.hidden = true; altBox.replaceChildren(); }
    }
    function offerOtherColours(values, statusText) {
      var chosen = chosenColour();
      if (!chosen || colourInputs.length < 2) return;
      var wanted = {};
      product.options.forEach(function (option, i) {
        var key = optionKey(option.name);
        if (key !== 'color' && key !== 'colour') wanted[key] = values[i];
      });
      if (!Object.keys(wanted).length) return;
      var token = ++altToken;
      if (!colourData[colourHandle(chosen)]) colourData[colourHandle(chosen)] = Promise.resolve(product);
      var others = colourInputs.filter(function (input) { return input !== chosen && !input.disabled; }).slice(0, ALT_LIMIT);
      /* A colour that is a variant of this product (most catalogue items: Color and Size options on one product) is
         checked in this product's own data, with no request; a colour sold as its own product is looked up. */
      var colourOption = product.options.find(function (option) { var key = optionKey(option.name); return key === 'color' || key === 'colour'; });
      var ownColours = colourOption && Array.isArray(colourOption.values) ? colourOption.values.map(String) : [];
      Promise.all(others.map(function (input) {
        if (ownColours.indexOf(input.value) > -1) {
          var withColour = Object.assign({}, wanted);
          withColour[optionKey(colourOption.name)] = input.value;
          return Promise.resolve(hasSelectionAvailable(product, withColour) ? input : null);
        }
        return colourProduct(input).then(function (data) { return hasSelectionAvailable(data, wanted) ? input : null; });
      })).then(function (matches) {
        if (token !== altToken || !root.isConnected) return;
        var found = matches.filter(Boolean);
        if (found.length) showAlternatives(found, statusText);
      });
    }
    function showAlternatives(found, statusText) {
      if (!altBox || !altBox.isConnected) {
        altBox = document.createElement('div');
        altBox.className = 'hfs-colour-alternatives';
        optionsRoot.insertAdjacentElement('afterend', altBox);
      }
      var labels = found.map(function (input) { return input.dataset.label || input.value; });
      var text = document.createElement('p');
      text.className = 'hfs-colour-alternatives__text';
      text.textContent = found.length === 1 ? 'Available in your size in this color:' : 'Available in your size in these colors:';
      var list = document.createElement('ul');
      list.className = 'hfs-colour-alternatives__list list-unstyled';
      list.setAttribute('role', 'list');
      found.forEach(function (input, i) {
        var item = document.createElement('li');
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'hfs-colour-alternatives__choice';
        button.textContent = labels[i];
        button.setAttribute('aria-label', 'Switch to ' + labels[i]);
        button.addEventListener('click', function () {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.focus();
        });
        item.appendChild(button);
        list.appendChild(item);
      });
      altBox.replaceChildren(text, list);
      altBox.hidden = false;
      announce((statusText ? statusText + '. ' : '') + 'Available in your size in ' + labels.join(', ') + '.');
    }
    function currentValues() {
      var colour = chosenColour();
      return product.options.map(function (option) {
        var key = optionKey(option.name);
        if (key === 'color' || key === 'colour') return colour ? colour.value : selected[key];
        return selected[key];
      });
    }
    function updateSelection() {
      var values = currentValues();
      setId('');
      detailToken++;
      clearAlternatives();
      optionsRoot.querySelectorAll('input[data-option-index]').forEach(function (input) {
        var index = Number(input.dataset.optionIndex);
        var available = product.variants.some(function (variant) {
          return variant.available && variant.options.every(function (value, i) {
            return i === index ? value === input.value : !values[i] || values[i] === value;
          });
        });
        input.classList.toggle('disabled', !available);
        syncOption(input);
      });
      if (values.some(function (value) { return value == null || value === ''; }) || (colourInputs.length && !chosenColour())) {
        setAddState(sectionId, false); // The Add button explains and focuses missing options.
        var payment = form.querySelector('.shopify-payment-button');
        if (payment) payment.style.display = 'none';
        return;
      }
      var variant = product.variants.find(function (candidate) {
        return candidate.options.length === values.length && candidate.options.every(function (value, i) { return value === values[i]; });
      });
      if (!variant || !variant.available) {
        setAddState(sectionId, true, variant ? strings().soldOut : strings().unavailable);
        announce(variant ? strings().soldOut : strings().unavailable);
        offerOtherColours(values, variant ? strings().soldOut : strings().unavailable);
        return;
      }
      // Keep Add unavailable until the displayed price and quantity rules correspond to this exact variant.
      setAddState(sectionId, true, 'Updating product…');
      var token = ++detailToken;
      fetch(productUrl(product.handle) + '?variant=' + variant.id + '&section_id=' + encodeURIComponent(renderingSectionId))
        .then(function (response) { if (!response.ok) throw new Error('Product details unavailable'); return response.text(); })
        .then(function (html) {
          if (token !== detailToken || !root.isConnected) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['price-', 'Sku-', 'Inventory-'].forEach(function (prefix) {
            var target = document.getElementById(prefix + sectionId);
            var source = doc.getElementById(prefix + renderingSectionId);
            if (target && !source) throw new Error('Product details incomplete');
            if (target && source) {
              target.innerHTML = source.innerHTML;
              target.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
            }
          });
          var quantity = form.elements.namedItem('quantity');
          var sourceQuantity = doc.getElementById('Quantity-' + renderingSectionId);
          if (quantity && sourceQuantity) {
            ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'].forEach(function (attr) {
              if (sourceQuantity.hasAttribute(attr)) quantity.setAttribute(attr, sourceQuantity.getAttribute(attr));
              else quantity.removeAttribute(attr);
            });
            if (!quantity.checkValidity() || !quantity.value) quantity.value = sourceQuantity.value;
            var quantityInput = quantity.closest('quantity-input');
            if (quantityInput && quantityInput.validateQtyRules) quantityInput.validateQtyRules();
          }
          var updatedButton = doc.getElementById('ProductSubmitButton-' + renderingSectionId);
          if (updatedButton && updatedButton.disabled) {
            setAddState(sectionId, true, strings().soldOut);
            announce(strings().soldOut);
            return;
          }
          setId(variant.id);
          var installment = document.querySelector('#product-form-installment-' + CSS.escape(sectionId) + ' [name="id"]');
          if (installment) { installment.value = variant.id; installment.dispatchEvent(new Event('change', { bubbles: true })); }
          var info = section.querySelector('product-info');
          if (info) {
            info.dataset.url = productUrl(product.handle);
            if (info.updateQuantityRules) info.updateQuantityRules(renderingSectionId, doc);
            if (info.setQuantityBoundries) info.setQuantityBoundries();
          }
          setAddState(sectionId, false);
          var errorForm = form.closest('product-form');
          if (errorForm && errorForm.handleErrorMessage) errorForm.handleErrorMessage();
        })
        .catch(function () {
          if (token !== detailToken || !root.isConnected) return;
          clearSelection();
          var errorForm = form.closest('product-form');
          if (errorForm && errorForm.handleErrorMessage) errorForm.handleErrorMessage('Could not update this option. Select it again to retry.');
          announce('Could not update this option. Select it again to retry.');
        });
    }
    function renderOptions() {
      optionsRoot.replaceChildren();
      var wanted = requested;
      requested = null; // once, for the link's colour: after that, colour changes keep the shopper's own choices
      product.options.forEach(function (option, index) {
        var key = optionKey(option.name);
        if ((key === 'color' || key === 'colour') && colourInputs.length) return;
        var values = option.values.map(String);
        // HFS (M1): the link's value when this colour offers it. When it does not, nothing is chosen in its place, not
        // even an only value, so the shopper reads the note and chooses.
        if (wanted && wanted[key] != null) selected[key] = values.includes(wanted[key]) ? wanted[key] : '';
        else if (!values.includes(selected[key])) selected[key] = values.length === 1 ? values[0] : '';
        var fieldset = document.createElement('fieldset');
        fieldset.className = 'js product-form__input';
        var legend = document.createElement('legend');
        legend.className = 'form__label';
        legend.textContent = 'Select ' + option.name + ': ';
        var echo = document.createElement('span');
        echo.textContent = selected[key];
        legend.appendChild(echo);
        fieldset.appendChild(legend);
        var error = document.createElement('div');
        error.id = 'hfs-option-error-' + sectionId + '-sub-' + index;
        error.className = 'product-form__error-message-wrapper hfs-option-error';
        error.dataset.optionError = option.name;
        error.style.display = 'none';
        error.setAttribute('role', 'alert');
        error.textContent = 'Please select an option';
        fieldset.appendChild(error);
        var euBrands = ['dansko', 'naot', 'arcopédico', 'arcopedico', 'spring step', 'spring step professional'];
        var isMens = (product.tags || []).some(function (tag) { return /^(mens|men's)$/i.test(tag); });
        var conversion = isMens ? {'41':'7.5-8','42':'8.5-9','43':'9.5-10','44':'10.5-11','45':'11.5-12','46':'12.5-13','47':'13.5-14','48':'14.5-15'} : {'35':'4.5-5','36':'5.5-6','37':'6.5-7','37.5':'7','38':'7.5-8','38.5':'8','39':'8.5-9','39.5':'9','40':'9.5-10','41':'10.5-11','42':'11.5-12','43':'12.5-13'};
        var useConversion = key === 'size' && euBrands.includes(String(product.vendor || '').toLowerCase());
        var mode = useConversion ? 'us' : 'eu';
        var displayValue = function (value) { return mode === 'us' && conversion[value] ? conversion[value] : value; };
        if (useConversion) fieldset.appendChild(sizeToggle(mode, function (newMode) {
          mode = newMode;
          fieldset.querySelectorAll('label[data-value]').forEach(function (label) { label.textContent = displayValue(label.dataset.value); });
          echo.textContent = displayValue(selected[key]);
          syncAllOptions(fieldset);
        }));
        values.forEach(function (value, valueIndex) {
          var input = document.createElement('input');
          input.type = 'radio';
          input.name = sectionId + '-sub-' + index;
          input.id = input.name + '-' + valueIndex;
          input.value = value;
          input.className = 'optionsName';
          input.dataset.optionName = option.name;
          input.dataset.optionIndex = index;
          input.checked = selected[key] === value;
          var label = document.createElement('label');
          label.htmlFor = input.id;
          label.dataset.value = value;
          label.textContent = displayValue(value);
          input.addEventListener('change', function () {
            if (window.hfsClearLinkNote) window.hfsClearLinkNote(root);
            selected[key] = value;
            echo.textContent = displayValue(value);
            setOptionError(option.name, false, fieldset);
            updateSelection();
          });
          // A checked option is an explicit retry after a failed details request.
          input.addEventListener('click', function () { if (!form.querySelector('[name="id"]').value && input.checked && selected[key] === value) updateSelection(); });
          fieldset.append(input, label);
        });
        echo.textContent = displayValue(selected[key]);
        optionsRoot.appendChild(fieldset);
        var guide = root.querySelector('template[data-hfs-guide="' + key + '"]');
        if (guide) optionsRoot.appendChild(guide.content.cloneNode(true));
      });
      // HFS (M1): say so when this colour does not offer an option the link asked for; nothing else is chosen instead
      if (wanted && Object.keys(wanted).some(function (key) { return selected[key] !== wanted[key]; }) && window.hfsLinkNote) window.hfsLinkNote(root);
      updateSelection();
    }
    function updateImage() {
      if (!product.featured_image) return;
      var colour = chosenColour();
      var alt = (product.media && product.media[0] && product.media[0].alt) || '';
      if (!alt || alt === product.title || alt === root.dataset.productTitle) alt = root.dataset.productTitle + (colour ? ' – ' + (colour.dataset.label || colour.value) : '');
      section.querySelectorAll('media-gallery').forEach(function (gallery) {
        var slot = gallery.querySelector('.product__media-item--variant');
        if (!slot) return;
        function swapImage(img) {
          var suffix = (img.alt.match(/ – image \d+ of \d+$/) || [''])[0];
          // HFS (general review 2026-10-01): keep each slot's own responsive sizes (gallery, thumbnail, zoom) instead
          // of one full-size URL for all of them.
          if (window.hfsSwapImage) window.hfsSwapImage(img, product.featured_image);
          else { img.src = product.featured_image; img.removeAttribute('srcset'); }
          img.alt = alt + (alt.endsWith(suffix) ? '' : suffix);
        }
        slot.querySelectorAll('.product__media img').forEach(swapImage);
        var thumbnail = gallery.querySelector('[data-target="' + slot.dataset.mediaId + '"] img');
        if (thumbnail) swapImage(thumbnail);
        var mediaId = slot.dataset.mediaId.slice(sectionId.length + 1);
        var modal = document.getElementById('ProductModal-' + sectionId);
        if (modal) modal.querySelectorAll('[data-media-id="' + mediaId + '"] img, img[data-media-id="' + mediaId + '"]').forEach(swapImage);
        if (gallery.setActiveMedia) gallery.setActiveMedia(slot.dataset.mediaId, false);
      });
    }
    function loadProduct(event) {
      var restoreOptionFocus = event && event.target && optionsRoot.contains(event.target);
      var colour = chosenColour();
      var token = ++loadToken;
      clearAlternatives();
      clearSelection(true); // HFS (FBL-006): options are not chosen yet while a colour is missing or loading
      if (controller) controller.abort();
      if (colourInputs.length && !colour) { status('Choose a color to see available options.'); return; }
      var requestController = new AbortController();
      controller = requestController;
      status('Loading product options…');
      root.setAttribute('aria-busy', 'true');
      var handle = root.dataset.productHandle + (colour && colour.dataset.subproduct ? '-' + colour.dataset.subproduct : '');
      fetch(productUrl(handle) + '.js', { signal: requestController.signal })
        .then(function (response) {
          if (token !== loadToken || !root.isConnected) throw Object.assign(new Error('Selection changed'), { name: 'AbortError' });
          if (response.status === 404 && handle !== root.dataset.productHandle) return fetch(productUrl(root.dataset.productHandle) + '.js', { signal: requestController.signal });
          return response;
        })
        .then(function (response) { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
        .then(function (data) {
          if (token !== loadToken || !root.isConnected) return;
          if (!Array.isArray(data.variants) || !Array.isArray(data.options)) throw new Error('Product options missing');
          product = data;
          renderOptions();
          updateImage();
          if (restoreOptionFocus) (optionsRoot.querySelector('input:checked, input') || chosenColour())?.focus();
          announce('Product options updated.');
        })
        .catch(function (error) {
          if (token !== loadToken || error.name === 'AbortError') return;
          status('Product options could not be loaded. Please retry.', true);
        })
        .finally(function () { if (token === loadToken) root.removeAttribute('aria-busy'); });
    }
    colourInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        requested = null; // HFS (M1): a colour the shopper picks before the link's colour has loaded starts unchosen
        if (window.hfsClearLinkNote) window.hfsClearLinkNote(root);
        setOptionError(input.dataset.optionName, false, root);
        var echo = input.closest('fieldset').querySelector('legend span');
        if (echo) echo.textContent = input.dataset.label || input.value;
        loadProduct();
      });
    });
    // HFS (SC11): a colour already chosen by the link (product-variant-options.liquid) is echoed in the legend too
    var startColour = chosenColour();
    var startEcho = startColour && startColour.closest('fieldset') && startColour.closest('fieldset').querySelector('legend span');
    if (startEcho && !startEcho.textContent) startEcho.textContent = startColour.dataset.label || startColour.value;
    // HFS (M1 complete variant intent): a link that names a variant asks for its size and width too
    // (hfsRequestedOptions, global.js). They are chosen when that colour's options load, if it offers them, and the
    // variant then resolves as if the shopper had chosen it: Sold out with other colours when it is. Card links and
    // Quick add ask for the colour only. A colour the shopper picks before then drops the link's choices.
    var request = window.hfsRequestedOptions ? window.hfsRequestedOptions(root) : { options: {}, stale: false };
    requested = Object.keys(request.options).length ? request.options : null;
    if (request.stale && window.hfsLinkNote) window.hfsLinkNote(root);
    loadProduct();
  }

  /* EU/US size system toggle (PDP-23/CSS-28): a labelled group of two buttons with aria-pressed, shown after the
     size <legend>. The pressed button is also underlined and bold (see .size-toggle-btn in custom-style.css), so the
     state is not carried by colour alone. onChange(mode) re-labels the size options. */
  function sizeToggle(initialMode, onChange) {
    var wrap = document.createElement('div');
    wrap.className = 'size-system-toggle';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Size system');
    var caption = document.createElement('span');
    caption.className = 'size-system-toggle__label';
    caption.setAttribute('aria-hidden', 'true');
    caption.textContent = 'Size system:';
    wrap.appendChild(caption);
    ['eu', 'us'].forEach(function (mode) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'size-toggle-btn';
      button.setAttribute('data-mode', mode);
      button.setAttribute('aria-pressed', mode === initialMode ? 'true' : 'false');
      if (mode === initialMode) button.classList.add('size-toggle-active');
      button.textContent = mode.toUpperCase();
      wrap.appendChild(button);
    });
    wrap.addEventListener('click', function (event) {
      var button = event.target.closest('.size-toggle-btn');
      if (!button || button.getAttribute('aria-pressed') === 'true') return;
      wrap.querySelectorAll('.size-toggle-btn').forEach(function (other) {
        var pressed = other === button;
        other.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        other.classList.toggle('size-toggle-active', pressed);
      });
      var mode = button.getAttribute('data-mode');
      if (onChange) onChange(mode);
      announce('Sizes shown in ' + mode.toUpperCase());
    });
    return wrap;
  }

  window.hfsPdp = {
    sizeToggle: sizeToggle,
    announce: announce,
    setAddState: setAddState,
    setOptionError: setOptionError,
    syncOptions: syncAllOptions,
    mountSplitPicker: mountSplitPicker,
  };

  statusRegion();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchOptions);
  else watchOptions();
})();
